import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, History, Loader2, RefreshCw } from "lucide-react";
import ReopenNameDialog from "@/components/admin/ReopenNameDialog";

type ReviewRow = {
  id: string;
  member_record_id: string;
  submitted_name: string;
  submitted_at: string;
  decision: string | null;
  note: string | null;
  decided_at: string | null;
};

type MemberLite = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  una_amount: string | null;
  monthly_amount: string | null;
  mea_signed_at: string | null;
  una_name_confirmed_at: string | null;
  chancery_interest: boolean | null;
};

function hoursSince(iso: string) {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
}

export default function NameReview() {
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [members, setMembers] = useState<Record<string, MemberLite>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [changeTarget, setChangeTarget] = useState<ReviewRow | null>(null);
  const [note, setNote] = useState("");
  const [historyFor, setHistoryFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("name_review_events")
      .select("*")
      .order("submitted_at", { ascending: true })
      .limit(500);

    if (error) {
      toast.error(error.message);
      setRows([]);
      setLoading(false);
      return;
    }

    const list = (data || []) as ReviewRow[];
    setRows(list);

    const ids = Array.from(new Set(list.map((r) => r.member_record_id)));
    if (ids.length) {
      const { data: mem } = await (supabase as any)
        .from("member_records")
        .select("id, first_name, last_name, email, una_amount, monthly_amount, mea_signed_at, una_name_confirmed_at, chancery_interest")
        .in("id", ids);
      const map: Record<string, MemberLite> = {};
      for (const m of (mem || []) as MemberLite[]) map[m.id] = m;
      setMembers(map);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = useMemo(() => rows.filter((r) => !r.decision), [rows]);

  const decide = async (row: ReviewRow, decision: "approved" | "change_requested", text: string) => {
    setBusyId(row.id);
    const { data, error } = await supabase.functions.invoke("name-review-decide", {
      body: { event_id: row.id, decision, note: text },
    });
    setBusyId(null);

    if (error || (data && (data as any).error)) {
      let message = "That did not go through. Please try again.";
      try {
        const parsed = await (error as any)?.context?.json?.();
        if (parsed?.error) message = parsed.error;
      } catch (_e) {
        if ((data as any)?.error) message = (data as any).error;
      }
      toast.error(message);
      return;
    }

    toast.success(decision === "approved" ? "Name approved." : "Change requested.");
    setChangeTarget(null);
    setNote("");
    void load();
  };

  const memberLabel = (id: string) => {
    const m = members[id];
    if (!m) return "Member";
    return `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim() || "Member";
  };

  const missingAmounts = (id: string) => {
    const m = members[id];
    if (!m) return false;
    return (!m.una_amount || !m.monthly_amount) && !m.mea_signed_at;
  };

  const historyRows = historyFor ? rows.filter((r) => r.member_record_id === historyFor) : [];

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Name review</h1>
          <p className="text-sm text-muted-foreground">
            Association names waiting on a decision from the team.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="min-h-11">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Waiting ({pending.length})</CardTitle>
          <CardDescription>Anything past 24 hours is marked in red.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : pending.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">The queue is clear.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Submitted name</TableHead>
                  <TableHead>Waiting</TableHead>
                  <TableHead className="text-right">Decision</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((row) => {
                  const hrs = hoursSince(row.submitted_at);
                  const prior = rows.filter(
                    (r) => r.member_record_id === row.member_record_id && r.id !== row.id,
                  ).length;
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">
                        {memberLabel(row.member_record_id)}
                        {prior > 0 && (
                          <Badge variant="outline" className="ml-2">
                            resubmission
                          </Badge>
                        )}
                        {members[row.member_record_id]?.una_name_confirmed_at && (
                          <Badge variant="secondary" className="ml-2">
                            Confirmed
                          </Badge>
                        )}
                        {members[row.member_record_id]?.chancery_interest === true && (
                          <div className="text-xs text-muted-foreground">
                            Open to an administration conversation
                          </div>
                        )}
                      </TableCell>

                      <TableCell className="text-muted-foreground">
                        {members[row.member_record_id]?.email ?? ""}
                        {missingAmounts(row.member_record_id) && (
                          <div className="text-xs text-amber-600 dark:text-amber-500">
                            No amounts on file, MEA will render blank
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{row.submitted_name}</TableCell>
                      <TableCell className={hrs >= 24 ? "font-semibold text-destructive" : ""}>
                        {hrs} h
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => decide(row, "approved", "")}
                            disabled={busyId === row.id}
                          >
                            <Check className="mr-1 h-4 w-4" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setChangeTarget(row);
                              setNote("");
                            }}
                            disabled={busyId === row.id}
                          >
                            Request change
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setHistoryFor(row.member_record_id)}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Decided</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Decision</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>When</TableHead>
                <TableHead className="text-right">Correction</TableHead>
              </TableRow>

            </TableHeader>
            <TableBody>
              {rows
                .filter((r) => r.decision)
                .slice()
                .reverse()
                .map((row) => {
                  const member = members[row.member_record_id];
                  const canReopen =
                    Boolean(member?.una_name_confirmed_at) || row.decision === "approved";
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{memberLabel(row.member_record_id)}</TableCell>
                      <TableCell>{row.submitted_name}</TableCell>
                      <TableCell>
                        <Badge variant={row.decision === "approved" ? "default" : "secondary"}>
                          {row.decision === "approved" ? "Approved" : "Change requested"}
                        </Badge>
                        {member?.una_name_confirmed_at && (
                          <Badge variant="secondary" className="ml-2">
                            Confirmed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-80 text-sm text-muted-foreground">
                        {row.note ?? ""}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {row.decided_at ? new Date(row.decided_at).toLocaleString() : ""}
                      </TableCell>
                      <TableCell className="text-right">
                        {canReopen && (
                          <ReopenNameDialog
                            memberRecordId={row.member_record_id}
                            memberName={memberLabel(row.member_record_id)}
                            onDone={load}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}

            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={Boolean(changeTarget)} onOpenChange={(o) => !o && setChangeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ask for a change</DialogTitle>
            <DialogDescription>
              Your note goes straight to the member and shows above the name field when they come
              back.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm">
              Submitted name: <span className="font-medium">{changeTarget?.submitted_name}</span>
            </p>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={5}
              placeholder="Write a short, warm note explaining the change you would like."
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setChangeTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => changeTarget && decide(changeTarget, "change_requested", note)}
              disabled={!note.trim() || busyId === changeTarget?.id}
            >
              Send the note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(historyFor)} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submission history</DialogTitle>
            <DialogDescription>
              {historyFor ? memberLabel(historyFor) : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {historyRows.map((r) => (
              <div key={r.id} className="rounded-lg border p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{r.submitted_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(r.submitted_at).toLocaleString()}
                  </span>
                </div>
                <p className="mt-1 text-muted-foreground">
                  {r.decision
                    ? r.decision === "approved"
                      ? "Approved"
                      : `Change requested: ${r.note ?? ""}`
                    : "Waiting"}
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
